import React, {Component} from "react";
import {Control, Field, Icon, Input} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import {getRedditPostDetails} from "../../../api/models/RedditPost";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import InputWarning from "../../../elements/InputWarning";
import SectionName from "../../../elements/SectionName";
import styled from "react-emotion";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import {INVALID_FIELD_STYLE, URL_REGEX} from "../../../utils/Constants";

const CSVButtonContainer = styled("div")`
    text-align: center;
    margin-top: 10px;
    margin-bottom: 10px;
`;

interface IRedditPostsModelState {
    loading: boolean;
    downloading: boolean;
    url: string;
    urlEmpty: boolean;
    urlInvalid: boolean;
    result: any;
    errorMessage: string;
}

class RedditPostsModel extends Component<{}, IRedditPostsModelState> {

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            downloading: false,
            url: "",
            urlEmpty: false,
            urlInvalid: false,
            result: null,
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.REDDIT_POSTS);
    }

    public handleUrlChange = (e:any) => {
        const value = e.target.value;

        let urlEmpty = false;
        let valid = true;
        if (value.trim()) {
            valid = URL_REGEX.test(value.trim());
        } else {
            urlEmpty = true;
        }

        this.setState({
            url: value,
            urlInvalid: !valid,
            urlEmpty,
            errorMessage: ""
        });
    };

    public handleUrlInputKeyPress = (e:any) => {
        const code = e.which || e.keyCode;
        if (code === 13) {
            this.onSubmit(null);
        }
    };

    public downloadCsvFile (e: any) {
        e.target.blur();

        if (!this.state.loading && this.state.result) {
            this.setState({
                downloading: true
            });

            const element = document.createElement("a");
            const file = new Blob([this.state.result], {type: 'text/csv'});
            element.href = URL.createObjectURL(file);
            element.download = "reddit_post.csv";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);

            this.setState({
                downloading: false
            });
        }
    }

    public onSubmit = async (e: any) => {
        if (e) {
            e.target.blur();
        }

        if (this.state.loading || !this.state.url.trim() || this.state.urlInvalid) {
            if (!this.state.url.trim()) {
                this.setState({
                    urlEmpty: true,
                    urlInvalid: false
                });
            }
            return;
        }

        this.setState({
            loading: true,
            downloading: false,
            result: null,
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getRedditPostDetails(this.state.url.trim()).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to get post."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            this.setState({
                loading: false,
                result: response.data
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to get post."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.REDDIT_POSTS);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        if (this.state.loading) {
            return;
        }

        this.setState({
            loading: false,
            downloading: false,
            url: "",
            urlEmpty: false,
            urlInvalid: false,
            result: null,
            errorMessage: ""
        });
    };

    public render() {
        return <ModelContentContainer>
            <Card>
                <SectionName>Reddit Post URL</SectionName>
                <Field>
                    <Control>
                        <Input
                            style={(this.state.urlEmpty || this.state.urlInvalid) ? INVALID_FIELD_STYLE : undefined}
                            type="text"
                            placeholder="Enter post url here"
                            value={this.state.url}
                            onChange={this.handleUrlChange}
                            onKeyPress={this.handleUrlInputKeyPress}
                        />
                        <div>
                            {
                                this.state.urlEmpty && <InputWarning>Please input URL.</InputWarning>
                            }
                            {
                                this.state.urlInvalid && <InputWarning>Please input a valid URL.</InputWarning>
                            }
                        </div>
                    </Control>
                </Field>
                <Spacer/>
                <div>
                    <Button onClick={this.onSubmit}>Submit</Button>
                    <Button onClick={this.resetState} style={{ marginLeft: "5px" }}>Reset</Button>
                </div>
                <Spacer/>
                {
                    <CSVButtonContainer>
                        {
                            this.state.loading ? <div>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin" />
                            </div> : <></>
                        }
                        {
                            this.state.downloading ? <div>
                                <span>Downloading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div> : <></>
                        }
                        {
                            !this.state.downloading && this.state.result ? <div>
                                <div>Reddit post details are ready to be downloaded as CSV file:</div>
                                <Spacer/>
                                <Button
                                    title="Download .csv file"
                                    style={{ marginRight: "5px" }}
                                    onClick={(e: any) => {
                                        this.downloadCsvFile(e);
                                    }}
                                >
                                    <Icon className="far fa-file-alt" style={{ marginRight: "5px" }}/>Download
                                </Button>
                            </div> : <></>
                        }
                    </CSVButtonContainer>
                }
                {
                    this.state.errorMessage && !this.state.loading ? <div>
                        <Spacer/>
                        <InformationCard>{this.state.errorMessage}</InformationCard>
                    </div> : <></>
                }
            </Card>
        </ModelContentContainer>;
    }
}

export default RedditPostsModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/RedditPosts/RedditPostsModel.tsx